import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Copy, Check, Loader2, Wand2, Sparkles, ArrowRight } from "lucide-react";
import { dmReply } from "@/lib/api";
import { toast } from "sonner";

const STAGE_COLOR = {
  Rapport: "#6B7280",
  Scoperta: "#2563EB",
  Qualifica: "#D97706",
  Invito: "#16A34A",
};

const STAGES = [
  {
    n: 1,
    key: "Rapport",
    title: "Rapport — rompi il ghiaccio",
    goal: "Falla sentire accolta. Zero vendita.",
    lines: [
      "Ciao [nome], piacere di conoscerti! Sono contento che il video ti abbia parlato. 💪",
      "Uscire da quel loop non è semplice, ma il fatto che tu lo stia già cercando attivamente dice molto.",
    ],
  },
  {
    n: 2,
    key: "Scoperta",
    title: "Scoperta — falla parlare",
    goal: "Capire il suo perché e il dolore vero. Domande APERTE, una alla volta. Mai supposizioni.",
    lines: [
      "Raccontami un attimo la tua situazione in questo periodo: cos'è che ti pesa di più?",
    ],
    branches: [
      { when: "Se dice: il TEMPO con i figli", reply: "Ti capisco. Raccontami com'è una tua giornata tipo adesso — così capisco meglio cosa ti pesa davvero." },
      { when: "Se dice: i SOLDI non bastano", reply: "Eh, ti capisco bene. Cosa vorresti che fosse diverso, se potessi cambiare una cosa sola?" },
      { when: "Se dice: qualcosa di MIO / realizzazione", reply: "Questa è la più importante. Cosa ti piacerebbe tornare a fare per te?" },
    ],
  },
  {
    n: 3,
    key: "Qualifica",
    title: "Qualifica — è pronta?",
    goal: "Capire se è aperta e disposta a metterci energia.",
    lines: [
      "Ti faccio una domanda diretta: se ci fosse un modo per costruirti una tua entrata, con i tuoi tempi e senza mollare la famiglia, saresti disposta a metterci un po' di energia per impararlo?",
    ],
  },
  {
    n: 4,
    key: "Invito",
    title: "Invito — al workshop",
    goal: "Solo quando è calda. La 'vendita' la fa l'evento, non tu in DM.",
    lines: [
      "Perfetto. Guarda, una volta al mese faccio un incontro gratuito dove spiego esattamente come tante donne come te si stanno costruendo una seconda entrata partendo da zero, senza mollare la famiglia. Ti va se ti mando i dettagli del prossimo?",
    ],
  },
];

const RULES = [
  "Mai vendere nei primi messaggi: prima la relazione, poi l'invito.",
  "Domande APERTE (cosa/come/raccontami), mai chiuse o quantificanti (quanto/da quanto/sì-no): la fanno raccontare, non la interrogano.",
  "Mai supposizioni: parti da ciò che ha scritto lei, con le sue parole. Non incasellarla né concludere al posto suo.",
  "Una domanda alla volta, messaggi brevi (no muri di testo).",
  "Rispecchia le SUE parole: falla sentire capita.",
  "Falla parlare più di te (ascolto > pitch).",
  "Invita al workshop solo quando è calda: l'evento vende, non tu.",
];

function CopyBtn({ text, testid }) {
  const [c, setC] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text || ""); setC(true); toast.success("Copiato"); setTimeout(() => setC(false), 1500); }}
      className="text-neutral-500 hover:text-white transition-colors shrink-0"
      title="Copia"
    >
      {c ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}

export default function DmScript() {
  const [leadMsg, setLeadMsg] = useState("");
  const [history, setHistory] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onSuggest = async () => {
    if (!leadMsg.trim()) { toast.error("Incolla l'ultimo messaggio della lead"); return; }
    setLoading(true);
    setResult(null);
    try {
      const data = await dmReply({ lead_message: leadMsg, history, note });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <MessageCircle size={22} className="text-[#D92D20]" />
          <h2 className="font-display font-extrabold text-3xl tracking-tight">Script DM</h2>
        </div>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          Trasforma le lead che ti scrivono in DM in iscritti al workshop. Usa l'assistente per la risposta al volo, o il copione fisso a 4 fasi.
        </p>
      </div>

      {/* Assistente AI */}
      <div className="bg-[#121212] border border-[#262626] rounded-md p-5 mb-8" data-testid="dm-assistant">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[#D92D20]" />
          <h3 className="font-display font-bold text-lg">Assistente risposta</h3>
        </div>
        <label className="label-caps block mb-1">Ultimo messaggio della lead</label>
        <textarea
          data-testid="dm-lead-message"
          value={leadMsg}
          onChange={(e) => setLeadMsg(e.target.value)}
          rows={3}
          placeholder="Incolla qui cosa ti ha scritto la lead..."
          className="w-full bg-[#0A0A0A] border border-[#333] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] resize-none mb-3"
        />
        <label className="label-caps block mb-1">Storico conversazione (facoltativo, per suggerimenti più precisi)</label>
        <textarea
          data-testid="dm-history"
          value={history}
          onChange={(e) => setHistory(e.target.value)}
          rows={4}
          placeholder={"Incolla tutta la chat, dal messaggio più vecchio al più recente. Es:\nLead: Ciao! Ho visto il tuo video...\nIo: Ciao [nome], piacere...\nLead: Faccio la commessa, non ho mai tempo..."}
          className="w-full bg-[#0A0A0A] border border-[#333] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] resize-none mb-3"
        />
        <label className="label-caps block mb-1">Note / contesto (facoltativo)</label>
        <input
          data-testid="dm-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Es. è mamma di 2 figli, prima conversazione, sembra molto motivata..."
          className="w-full bg-[#0A0A0A] border border-[#333] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4"
        />
        <button
          data-testid="dm-suggest-btn"
          onClick={onSuggest}
          disabled={loading}
          className="bg-[#D92D20] hover:bg-[#B42318] disabled:opacity-60 text-white font-bold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm uppercase tracking-wider"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
          {loading ? "Genero..." : "Suggerisci risposta"}
        </button>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 border-t border-[#262626] pt-4"
            data-testid="dm-result"
          >
            {result.fase && (
              <span
                className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm"
                style={{ background: `${STAGE_COLOR[result.fase] || "#6B7280"}22`, color: STAGE_COLOR[result.fase] || "#9CA3AF" }}
              >
                Fase: {result.fase}
              </span>
            )}
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4 mt-3 flex items-start gap-3">
              <p className="text-neutral-100 leading-relaxed whitespace-pre-wrap flex-1" data-testid="dm-reply-text">{result.risposta}</p>
              <CopyBtn text={result.risposta} testid="dm-copy-reply" />
            </div>
            {result.consiglio && (
              <p className="text-xs text-neutral-500 italic mt-2">💡 {result.consiglio}</p>
            )}
          </motion.div>
        )}
      </div>

      {/* Copione fisso a 4 fasi */}
      <h3 className="font-display font-bold text-xl mb-1">Copione — il funnel a 4 fasi</h3>
      <p className="text-sm text-neutral-500 mb-5">Frasi pronte da copiare. Non saltare le fasi, una domanda alla volta.</p>

      <div className="space-y-4">
        {STAGES.map((s) => (
          <div key={s.key} className="bg-[#121212] border border-[#262626] rounded-md p-5" data-testid={`dm-stage-${s.key}`}>
            <div className="flex items-center gap-3 mb-1">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center font-display font-extrabold text-sm shrink-0"
                style={{ background: `${STAGE_COLOR[s.key]}22`, color: STAGE_COLOR[s.key] }}
              >
                {s.n}
              </span>
              <h4 className="font-display font-bold text-lg">{s.title}</h4>
            </div>
            <p className="text-xs text-neutral-500 mb-3 ml-10">{s.goal}</p>

            <div className="space-y-2 ml-10">
              {s.lines.map((line, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 flex items-start gap-3">
                  <p className="text-sm text-neutral-200 leading-relaxed flex-1">{line}</p>
                  <CopyBtn text={line} testid={`dm-copy-${s.key}-${i}`} />
                </div>
              ))}

              {s.branches && (
                <div className="pt-2 space-y-2">
                  <p className="text-[0.7rem] uppercase tracking-wider text-neutral-600 font-semibold">In base alla sua risposta:</p>
                  {s.branches.map((b, i) => (
                    <div key={i} className="border-l-2 pl-3" style={{ borderColor: STAGE_COLOR[s.key] }}>
                      <p className="text-xs text-neutral-500 mb-1">{b.when}</p>
                      <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 flex items-start gap-3">
                        <p className="text-sm text-neutral-200 leading-relaxed flex-1">{b.reply}</p>
                        <CopyBtn text={b.reply} testid={`dm-copy-branch-${s.key}-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Regole d'oro */}
      <div className="bg-[#121212] border border-[#262626] rounded-md p-5 mt-6">
        <h4 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <ArrowRight size={16} className="text-[#D92D20]" /> Regole d'oro
        </h4>
        <ul className="space-y-2">
          {RULES.map((r, i) => (
            <li key={i} className="text-sm text-neutral-300 flex items-start gap-2">
              <span className="text-[#D92D20] mt-0.5">•</span> {r}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
